<script setup lang="ts">
    import { inject, onMounted, reactive, ref } from 'vue';
    import axios from 'axios';
    import { useRouter } from 'vue-router';
    import moment from 'moment';
    import CustomPhoneInput from '@/components/CustomPhoneInput.vue';
    import { showAlert } from '@/utils/swal';

    const router = useRouter();
    const BACKEND_URL = inject('BACKEND_URL');
    const errorMessage = ref('');
    const loading = ref(false);
    const pageLoading = ref(true);

    const logoFile = ref<File | null>(null);
    const logoPreview = ref<string | null>(null);
    const existingLogo = ref<string | null>(null);

    const startupFormDatas = reactive({
        pays: [] as any[],
    });

    const formData = reactive({
        id: null as number | null,
        nom: '',
        rccm: '',
        adresse: '',
        telephone: '',
        email: '',
        date_creation: '',
        pays_id: null as number | null,
        secteurs: [] as string[],
    });

    const errors = reactive({
        nom: '',
        rccm: '',
        adresse: '',
        telephone: '',
        email: '',
        date_creation: '',
        pays_id: '',
        secteurs: '',
    });

    const secteurs = [
        { id: 'finance', value: 'Finance', label: 'Finance' },
        { id: 'informatique', value: 'Informatique', label: 'Informatique' },
        { id: 'agriculture', value: 'Agriculture', label: 'Agriculture' },
        { id: 'education', value: 'Éducation', label: 'Éducation' },
        { id: 'sante', value: 'Santé', label: 'Santé' },
        { id: 'transport', value: 'Transport', label: 'Transport' },
        { id: 'construction', value: 'Construction', label: 'Construction' },
        { id: 'commerce', value: 'Commerce', label: 'Commerce' },
        { id: 'industrie', value: 'Industrie', label: 'Industrie' },
        { id: 'autre', value: 'Autre', label: 'Autre' },
    ];

    function normalizeSecteurs(raw: any): string[] {
        if (!Array.isArray(raw)) return [];
        const canonical = new Map(secteurs.map(s => [s.value.toLowerCase(), s.value]));
        return raw.map((v: any) => {
            const key = String(v).toLowerCase();
            return canonical.get(key) ?? String(v);
        });
    }

    onMounted(async () => {
        try {
            const response = await axios.get('/collectivites/ma-collectivite/form_datas');
            const { collectivite, startupFormDatas: startup } = response.data;
            Object.assign(startupFormDatas, startup);
            if (collectivite) {
                if (collectivite.date_creation) {
                    const date = moment(collectivite.date_creation);
                    collectivite.date_creation = date.isValid() ? date.format('YYYY-MM-DD') : '';
                }
                Object.assign(formData, collectivite);
                formData.secteurs = normalizeSecteurs(collectivite.secteurs);
                existingLogo.value = collectivite.logo || null;
            }
        } catch (e: any) {
            errorMessage.value = e.response?.data?.message || 'Impossible de charger la collectivité';
        } finally {
            pageLoading.value = false;
        }
    });

    function onLogoSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;
        logoFile.value = file;
        const reader = new FileReader();
        reader.onload = (e) => { logoPreview.value = e.target?.result as string; };
        reader.readAsDataURL(file);
    }

    function clearLogo() {
        logoFile.value = null;
        logoPreview.value = null;
    }

    function validate() {
        let isValid = true;

        errors.nom = '';
        if (!formData.nom) { errors.nom = 'Le nom est requis'; isValid = false; }

        errors.pays_id = '';
        if (!formData.pays_id || formData.pays_id <= 0) { errors.pays_id = 'Le pays est requis'; isValid = false; }

        errors.rccm = '';
        if (!formData.rccm) { errors.rccm = 'Le RCCM est requis'; isValid = false; }

        errors.adresse = '';
        if (!formData.adresse) { errors.adresse = "L'adresse est requise"; isValid = false; }

        errors.telephone = '';
        if (!formData.telephone) { errors.telephone = 'Le téléphone est requis'; isValid = false; }

        errors.email = '';
        if (!formData.email) {
            errors.email = "L'email est requis";
            isValid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = "Le format de l'email est invalide";
            isValid = false;
        }

        errors.secteurs = '';
        if (!formData.secteurs || formData.secteurs.length === 0) {
            errors.secteurs = 'Au moins un secteur est requis';
            isValid = false;
        }

        errors.date_creation = '';
        if (!formData.date_creation) { errors.date_creation = 'La date de création est requise'; isValid = false; }

        return isValid;
    }

    async function onSubmit() {
        if (!validate()) return;
        loading.value = true;
        errorMessage.value = '';
        try {
            const payload = new FormData();
            payload.append('nom', formData.nom);
            payload.append('rccm', formData.rccm);
            payload.append('adresse', formData.adresse);
            payload.append('telephone', formData.telephone);
            payload.append('email', formData.email);
            payload.append('date_creation', formData.date_creation);
            payload.append('pays_id', String(formData.pays_id ?? ''));
            formData.secteurs.forEach((s, i) => payload.append(`secteurs[${i}]`, s));
            if (logoFile.value) {
                payload.append('logo', logoFile.value);
            }

            await axios.post('/collectivites/ma-collectivite/update', payload, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            showAlert.success('Collectivité mise à jour avec succès');
            router.push('/front/collectivites/ma-collectivite');
        } catch (error: any) {
            errorMessage.value = error.response?.data?.message || error.message || 'Une erreur est survenue';
            console.error(error);
        } finally {
            loading.value = false;
        }
    }

    function onCancel() {
        router.push('/front/collectivites/ma-collectivite');
    }
</script>

<template>
    <section class="container-full-1 bg-grey-50">
        <div class="edit-header">
            <div class="edit-header-left">
                <button type="button" class="btn white2" @click="onCancel">
                    <img src="/public/icons/icon-back.svg" class="btn-icon" alt="" />
                    <span>Retour</span>
                </button>
                <div>
                    <p class="text-sm text-grey-500" style="margin: 0;">Collectivité</p>
                    <h4 class="font-semibold" style="margin: 0;">Modifier ma collectivité</h4>
                </div>
            </div>
            <div class="edit-header-actions">
                <button type="button" class="btn lg white2" @click="onCancel" :disabled="loading">Annuler</button>
                <button type="button" class="btn lg" @click="onSubmit" :disabled="loading || pageLoading">
                    <span v-if="!loading">Enregistrer</span>
                    <span v-else>Enregistrement...</span>
                </button>
            </div>
        </div>

        <div v-if="pageLoading" class="flex-center py-10">
            <div class="loader dark"></div>
        </div>

        <div v-else>
            <div v-if="errorMessage" class="alert alert-danger mb-4">{{ errorMessage }}</div>

            <div class="edit-grid">
                <!-- Colonne principale -->
                <div class="edit-main">
                    <div class="widget edit-card">
                        <h3 class="edit-card-title">
                            <img src="/public/icons/icon-info-collectivite.svg" class="size-base" alt="" />
                            Informations
                        </h3>

                        <div class="form-field-floating lg reactive active">
                            <input type="text" class="form-input" v-model="formData.nom" placeholder="" />
                            <label>Désignation de la collectivité <b class="color-danger">*</b></label>
                        </div>
                        <span v-if="errors.nom" class="text-red">{{ errors.nom }}</span>

                        <div class="edit-row">
                            <div>
                                <div class="form-field-floating lg reactive active">
                                    <input type="text" class="form-input" v-model="formData.rccm" placeholder="" />
                                    <label>Numéro du registre de commerce <b class="color-danger">*</b></label>
                                </div>
                                <span v-if="errors.rccm" class="text-red">{{ errors.rccm }}</span>
                            </div>
                            <div>
                                <div class="form-field-floating active">
                                    <select class="form-input-select" v-model="formData.pays_id">
                                        <option :value="null">Sélectionnez un pays</option>
                                        <option :value="pays.id" v-for="(pays, index) in startupFormDatas.pays" :key="'pays_' + index">{{ pays.nom }}</option>
                                    </select>
                                    <label>Pays <b class="color-danger">*</b></label>
                                </div>
                                <span v-if="errors.pays_id" class="text-red">{{ errors.pays_id }}</span>
                            </div>
                        </div>

                        <div class="form-field-floating lg reactive">
                            <input type="text" class="form-input" v-model="formData.adresse" placeholder="" />
                            <label>Adresse de l'entreprise <b class="color-danger">*</b></label>
                        </div>
                        <span v-if="errors.adresse" class="text-red">{{ errors.adresse }}</span>

                        <div class="edit-row">
                            <div>
                                <custom-phone-input v-model="formData.telephone" id="telephone" />
                                <span v-if="errors.telephone" class="text-red">{{ errors.telephone }}</span>
                            </div>
                            <div>
                                <div class="form-field-floating lg reactive">
                                    <input type="email" class="form-input" v-model="formData.email" placeholder="" />
                                    <label>Adresse Email <b class="color-danger">*</b></label>
                                </div>
                                <span v-if="errors.email" class="text-red">{{ errors.email }}</span>
                            </div>
                        </div>

                        <div class="form-field-floating lg reactive">
                            <input type="date" class="form-input" v-model="formData.date_creation" />
                            <label>Date de création</label>
                        </div>
                        <span v-if="errors.date_creation" class="text-red">{{ errors.date_creation }}</span>
                    </div>

                    <div class="widget edit-card">
                        <h3 class="edit-card-title">
                            <img src="/public/icons/icon-secteur-activite.svg" class="size-base" alt="" />
                            Secteur d'activité
                        </h3>
                        <p class="text-sm text-grey-500 mt--2 mb-4">Sélectionnez un ou plusieurs secteurs.</p>

                        <div class="secteurs-grid">
                            <label v-for="secteur in secteurs" :key="secteur.id" class="secteur-chip" :class="{ active: formData.secteurs.includes(secteur.value) }" :for="`secteur_${secteur.id}`">
                                <input type="checkbox" :id="`secteur_${secteur.id}`" :value="secteur.value" v-model="formData.secteurs" />
                                <span>{{ secteur.label }}</span>
                            </label>
                        </div>
                        <span v-if="errors.secteurs" class="text-red">{{ errors.secteurs }}</span>
                    </div>
                </div>

                <!-- Colonne latérale : logo -->
                <aside class="edit-side">
                    <div class="widget edit-card">
                        <h3 class="edit-card-title">
                            <img src="/public/icons/icon-info-collectivite.svg" class="size-base" alt="" />
                            Logo
                        </h3>
                        <div class="logo-uploader">
                            <div class="logo-preview">
                                <img v-if="logoPreview" :src="logoPreview" alt="Aperçu" />
                                <img v-else-if="existingLogo" :src="`${BACKEND_URL}/assets/collectivites/${existingLogo}`" alt="Logo actuel" />
                                <span v-else class="logo-placeholder">Aucun logo</span>
                            </div>
                            <div class="logo-actions">
                                <label for="logo-input" class="btn white2 active" style="cursor: pointer;">
                                    <img src="/public/icons/icon-upload.svg" class="btn-icon" alt="" />
                                    <span>{{ existingLogo || logoFile ? 'Remplacer le logo' : 'Choisir un logo' }}</span>
                                </label>
                                <input id="logo-input" type="file" accept="image/*" style="display: none;" @change="onLogoSelected" />
                                <button v-if="logoFile" type="button" class="btn white2" @click="clearLogo">
                                    Annuler la sélection
                                </button>
                            </div>
                            <p class="text-sm text-grey-500" style="margin: 0;">PNG, JPG, SVG · max 5 Mo. Format carré recommandé.</p>
                        </div>
                    </div>
                </aside>
            </div>

            <div class="edit-footer-actions">
                <button type="button" class="btn lg white2" @click="onCancel" :disabled="loading">Annuler</button>
                <button type="button" class="btn lg" @click="onSubmit" :disabled="loading">
                    <span v-if="!loading">Enregistrer les modifications</span>
                    <span v-else>Enregistrement...</span>
                </button>
            </div>
        </div>
    </section>
</template>

<style scoped>
.edit-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
    padding: 0 0.25rem;
    margin-bottom: 1.5rem;
}

.edit-header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.edit-header-actions {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
}

.edit-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 320px);
    gap: 1.25rem;
    align-items: start;
}

@media (max-width: 960px) {
    .edit-grid {
        grid-template-columns: 1fr;
    }
}

.edit-main {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    min-width: 0;
}

.edit-side {
    min-width: 0;
}

.edit-card {
    padding: 1.75rem;
}

.edit-card + .edit-card {
    margin-top: 0;
}

.edit-card-title {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: rgb(var(--primary-color-rgb), 1);
    font-weight: 600;
    margin: 0 0 1.25rem;
}

.edit-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
    margin: 0.75rem 0;
}

@media (max-width: 600px) {
    .edit-row {
        grid-template-columns: 1fr;
    }
}

.secteurs-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 0.6rem;
}

.secteur-chip {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.6rem 0.9rem;
    border-radius: 12px;
    background-color: rgba(0, 0, 0, 0.03);
    border: 2px solid transparent;
    cursor: pointer;
    transition: all 0.15s ease;
    font-size: 0.9rem;
    font-weight: 500;
}

.secteur-chip:hover {
    background-color: rgba(0, 0, 0, 0.05);
}

.secteur-chip.active {
    background-color: rgba(var(--primary-color-rgb), 0.12);
    border-color: rgba(var(--primary-color-rgb), 0.6);
    color: rgb(var(--primary-color-rgb), 1);
}

.secteur-chip input[type="checkbox"] {
    accent-color: rgb(var(--primary-color-rgb));
    margin: 0;
}

.logo-uploader {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
}

.logo-preview {
    width: 100%;
    aspect-ratio: 1;
    border-radius: 16px;
    overflow: hidden;
    background-color: rgba(0, 0, 0, 0.04);
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px dashed rgba(0, 0, 0, 0.1);
}

.logo-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.logo-placeholder {
    color: rgba(0, 0, 0, 0.4);
    font-size: 0.9rem;
}

.logo-actions {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.edit-footer-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    flex-wrap: wrap;
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
}
</style>
